import { Box, Button, Flex, Menu, Select, Stack, Text } from '@mantine/core';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateSectionCreationData } from 'src/company/templates/types';
import { SectionListItem } from './SectionListItem';
import { SectionType, TemplateSection } from 'src/data-capture/hooks/templates/useTemplates';

type Props<T extends TemplateSectionCreationData | TemplateSection> = {
  sections: T[];
  selectedSectionId?: string;
  onSectionSelect?: (sectionId: string) => void;
  showLevelSelector?: boolean;
  sectionLevel?: number;
  onSectionLevelChange?: (level: number) => void;
  isSectionCompleted: (section: T) => boolean;
  onToggleComplete: (sectionId: string) => void;
  onSectionReorder?: (fromIndex: number, toIndex: number) => void;
  autoNavigateOnComplete?: boolean;
  onDeleteSection?: (section: T) => void;
  onMergeSection?: (section: T) => void;
  onAddSection?: (sectionType: SectionType) => void;
  canAddTableOfContents?: boolean;
  onMarkAllComplete?: () => void;
  onEditSectionHeader?: (section: T, newHeader: string) => void;
  onSectionHeaderLevelChange?: (section: T, newLevel: number) => void;
  renderContent: (
    selectedSection: T,
    onToggleComplete: (sectionId: string) => void,
  ) => React.ReactNode;
  sectionAction?: {
    onClick: (section: T) => void;
    label: string;
    showCondition?: (section: T) => boolean;
  };
  clientSelection?: {
    label: string;
    placeholder: string;
    data: Array<{ value: string; label: string }>;
    value: string | null;
    onChange: (value: string | null) => void;
    disabled?: boolean;
  };
  tenseSelection?: {
    label: string;
    description?: string;
    data: Array<{ value: string; label: string }>;
    value: string;
    onChange: (value: string | null) => void;
  };
};

const LEFT_PANEL_DEFAULT_WIDTH = 280;
const LEFT_PANEL_MIN_WIDTH = 240;
const RIGHT_PANEL_MIN_WIDTH = 320;
const RESIZE_HANDLE_WIDTH = 8;

const clampNumericValue = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export function SectionEditorLayout<T extends TemplateSectionCreationData | TemplateSection>({
  sections,
  selectedSectionId: propSelectedSectionId,
  onSectionSelect,
  showLevelSelector = false,
  sectionLevel = 2,
  onSectionLevelChange,
  isSectionCompleted,
  onToggleComplete,
  onSectionReorder,
  autoNavigateOnComplete = true,
  renderContent,
  onEditSectionHeader,
  onDeleteSection,
  onMergeSection,
  onAddSection,
  canAddTableOfContents = false,
  onMarkAllComplete,
  onSectionHeaderLevelChange,
  clientSelection,
  tenseSelection,
}: Props<T>) {
  const [internalSelectedSectionId, setInternalSelectedSectionId] = useState<string>(
    sections[0]?.id || '',
  );
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(LEFT_PANEL_DEFAULT_WIDTH);
  const [isResizingLeftPanel, setIsResizingLeftPanel] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isResizingLeftPanelRef = useRef(false);
  const bodyUserSelectRef = useRef<string>('');
  const bodyCursorRef = useRef<string>('');

  const clampLeftPanelWidth = useCallback((width: number) => {
    if (!containerRef.current) {
      return Math.max(width, LEFT_PANEL_MIN_WIDTH);
    }

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const computedStyles = window.getComputedStyle(container);
    const gapRaw = parseFloat(computedStyles.columnGap || computedStyles.gap || '0');
    const gapValue = Number.isFinite(gapRaw) ? gapRaw : 0;
    const totalFixedWidth = RESIZE_HANDLE_WIDTH + gapValue * 2;
    const maxPanelWidth = Math.max(
      LEFT_PANEL_MIN_WIDTH,
      containerRect.width - RIGHT_PANEL_MIN_WIDTH - totalFixedWidth,
    );

    return clampNumericValue(width, LEFT_PANEL_MIN_WIDTH, maxPanelWidth);
  }, []);

  const selectedSectionId = propSelectedSectionId ?? internalSelectedSectionId;
  const handleSectionSelect = (sectionId: string) => {
    if (onSectionSelect) {
      onSectionSelect(sectionId);
    } else {
      setInternalSelectedSectionId(sectionId);
    }
  };

  const selectedSection = sections.find(s => s.id === selectedSectionId);

  const handleSectionReorder = (fromIndex: number, toIndex: number) => {
    if (onSectionReorder) {
      onSectionReorder(fromIndex, toIndex);
    }
    setDragOverIndex(null);
  };

  const handleDragOver = (index: number) => {
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const stopLeftPanelResizing = useCallback(() => {
    if (!isResizingLeftPanelRef.current) {
      return;
    }

    isResizingLeftPanelRef.current = false;
    setIsResizingLeftPanel(false);

    if (bodyUserSelectRef.current) {
      document.body.style.userSelect = bodyUserSelectRef.current;
    } else {
      document.body.style.removeProperty('user-select');
    }

    if (bodyCursorRef.current) {
      document.body.style.cursor = bodyCursorRef.current;
    } else {
      document.body.style.removeProperty('cursor');
    }
  }, []);

  useEffect(() => {
    if (!isResizingLeftPanel) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizingLeftPanelRef.current || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const computedStyles = window.getComputedStyle(container);
      const gapRaw = parseFloat(computedStyles.columnGap || computedStyles.gap || '0');
      const gapValue = Number.isFinite(gapRaw) ? gapRaw : 0;
      const totalFixedWidth = RESIZE_HANDLE_WIDTH + gapValue * 2;
      const maxPanelWidth = Math.max(
        LEFT_PANEL_MIN_WIDTH,
        containerRect.width - RIGHT_PANEL_MIN_WIDTH - totalFixedWidth,
      );

      let nextWidth = event.clientX - containerRect.left - gapValue - RESIZE_HANDLE_WIDTH / 2;
      if (Number.isFinite(nextWidth)) {
        nextWidth = clampNumericValue(nextWidth, LEFT_PANEL_MIN_WIDTH, maxPanelWidth);
        setLeftPanelWidth(nextWidth);
      }
    };

    const handleMouseUp = () => {
      stopLeftPanelResizing();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      stopLeftPanelResizing();
    };
  }, [isResizingLeftPanel, stopLeftPanelResizing]);

  useEffect(() => {
    const clampWidthToBounds = () => {
      if (!containerRef.current) {
        return;
      }

      setLeftPanelWidth(prev => clampLeftPanelWidth(prev));
    };

    clampWidthToBounds();
    window.addEventListener('resize', clampWidthToBounds);

    return () => {
      window.removeEventListener('resize', clampWidthToBounds);
    };
  }, [clampLeftPanelWidth]);

  const handleLeftPanelResizeMouseDown = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) {
      return;
    }

    event.preventDefault();

    bodyUserSelectRef.current = document.body.style.userSelect || '';
    bodyCursorRef.current = document.body.style.cursor || '';

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    isResizingLeftPanelRef.current = true;
    setIsResizingLeftPanel(true);
  }, []);

  const handleLeftPanelResizeDoubleClick = useCallback(() => {
    setLeftPanelWidth(clampLeftPanelWidth(LEFT_PANEL_DEFAULT_WIDTH));
  }, [clampLeftPanelWidth]);

  const handleToggleComplete = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) {
      return;
    }

    const isCompleting = !isSectionCompleted(section);

    onToggleComplete(sectionId);

    if (!autoNavigateOnComplete || !isCompleting) {
      return;
    }

    // Auto-navigate to 'next' incomplete section, if any. Checks prior if none after.
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    const laterIncompleteSectionIdx = sections
      .slice(currentIndex + 1)
      .findIndex(s => !isSectionCompleted(s));

    if (laterIncompleteSectionIdx > -1) {
      const nextIncompleteSection = sections.slice(currentIndex + 1)[laterIncompleteSectionIdx];
      handleSectionSelect(nextIncompleteSection.id);
      return;
    }

    const priorIncompleteSectionIdx = sections
      .slice(0, currentIndex)
      .findIndex(s => !isSectionCompleted(s));
    if (priorIncompleteSectionIdx > -1) {
      const priorIncompleteSection = sections.slice(0, currentIndex)[priorIncompleteSectionIdx];
      handleSectionSelect(priorIncompleteSection.id);
    }
  };

  return (
    <Box
      style={{
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
      h="100%"
    >
      <Flex ref={containerRef} h="100%" gap="md">
        <Stack
          gap="xs"
          h="100%"
          style={{
            width: leftPanelWidth,
            minWidth: LEFT_PANEL_MIN_WIDTH,
            flex: '0 0 auto',
          }}
        >
          {clientSelection && (
            <Box>
              <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="xs">
                {clientSelection.label}
              </Text>
              <Select
                placeholder={clientSelection.placeholder}
                data={clientSelection.data}
                value={clientSelection.value}
                onChange={clientSelection.onChange}
                searchable
                disabled={clientSelection.disabled}
              />
            </Box>
          )}

          {tenseSelection && (
            <Box>
              <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="xs">
                {tenseSelection.label}
              </Text>
              {tenseSelection.description && (
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6" mb="xs">
                  {tenseSelection.description}
                </Text>
              )}
              <Select
                data={tenseSelection.data}
                value={tenseSelection.value}
                onChange={tenseSelection.onChange}
              />
            </Box>
          )}

          <Box>
            <Flex align="center" justify="space-between" gap="md">
              <Box>
                <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
                  Sections
                </Text>
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
                  Click to edit • Drag to reorder
                </Text>
              </Box>
              {showLevelSelector && onSectionLevelChange && (
                <Flex align="center" gap="xxxs">
                  <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Split by
                  </Text>
                  <Select
                    size="xs"
                    value={sectionLevel.toString()}
                    onChange={value => value && onSectionLevelChange(parseInt(value))}
                    data={[
                      { value: '1', label: 'H1' },
                      { value: '2', label: 'H2' },
                      { value: '3', label: 'H3' },
                      { value: '4', label: 'H4' },
                      { value: '5', label: 'H5' },
                      { value: '6', label: 'H6' },
                    ]}
                    w={60}
                    styles={{ input: { textAlign: 'center' } }}
                  />
                </Flex>
              )}
            </Flex>
          </Box>

          <Stack
            gap="xxs"
            flex={1}
            style={{ overflowY: 'auto', overflowX: 'hidden' }}
            onDragLeave={handleDragLeave}
          >
            {sections.map((section, index) => (
              <SectionListItem
                key={section.id}
                section={{ ...section, isCompleted: isSectionCompleted(section) }}
                isSelected={section.id === selectedSectionId}
                onSelect={() => handleSectionSelect(section.id)}
                enableDragDrop={!!onSectionReorder}
                dragIndex={dragOverIndex}
                itemIndex={index}
                onDragReorder={handleSectionReorder}
                onDragEnterItem={handleDragOver}
                onEditSectionHeader={onEditSectionHeader}
                onDeleteSection={onDeleteSection}
                onMergeSection={onMergeSection}
                onChangeSectionHeaderLevel={onSectionHeaderLevelChange}
              />
            ))}
          </Stack>

          {onMarkAllComplete && sections.some(s => !isSectionCompleted(s)) && (
            <Button
              variant="light"
              size="sm"
              leftSection={<IconCheck size={16} />}
              onClick={onMarkAllComplete}
              fullWidth
              style={{ flexShrink: 0 }}
            >
              Mark all as complete
            </Button>
          )}

          {onAddSection &&
            (canAddTableOfContents ? (
              <Menu position="top" withinPortal>
                <Menu.Target>
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconPlus size={16} />}
                    fullWidth
                    style={{ flexShrink: 0 }}
                  >
                    Add section
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => onAddSection('CONTENT')}>Content section</Menu.Item>
                  <Menu.Item onClick={() => onAddSection('TABLE_OF_CONTENTS')}>
                    Table of contents
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Button
                variant="light"
                size="sm"
                leftSection={<IconPlus size={16} />}
                onClick={() => onAddSection('CONTENT')}
                fullWidth
                style={{ flexShrink: 0 }}
              >
                Add section
              </Button>
            ))}
        </Stack>

        <Box
          role="separator"
          aria-orientation="vertical"
          tabIndex={0}
          onMouseDown={handleLeftPanelResizeMouseDown}
          onDoubleClick={handleLeftPanelResizeDoubleClick}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleLeftPanelResizeDoubleClick();
            }
          }}
          style={{
            width: RESIZE_HANDLE_WIDTH,
            cursor: 'col-resize',
            backgroundColor: isResizingLeftPanel
              ? 'var(--mantine-color-gray-2)'
              : 'var(--mantine-color-gray-1)',
            borderRadius: 4,
            flex: '0 0 auto',
            alignSelf: 'stretch',
          }}
        />

        <Stack h="100%" miw={0} flex={1}>
          {selectedSection && renderContent(selectedSection, handleToggleComplete)}
        </Stack>
      </Flex>
    </Box>
  );
}
